import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Chip, Grid2, Tab, Tabs, Typography } from '@mui/material';
import EditUserProfileTab from './components/EditUserProfileTab';
import EditUserSecurityTab from './components/EditUserSecurityTab';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import { useAuth } from '../../hooks/useAuth';
import { useCapabilities } from '../../hooks/useCapabilities';
import useNotify from '../../hooks/useNotify';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import PageWithTitle from '../../components/atom/PageWithTitle';
import {
    ROLE_DEFAULTS,
    TEMPLATE_ROLES,
    getRoleLabelKey,
    getAssignableTemplates,
    canManageTarget,
} from '../../helpers/capabilities';
import { useTranslation } from 'react-i18next';
import {
    useAddUser,
    useEditUser,
    useGetUser,
    getGetUserQueryKey,
    getGetUsersQueryKey,
    useForceSetUserPassword,
} from '@openapi/users/users';
import { useResetDefaultPassword, useResetTwoFactor } from '@openapi/auth/auth';
import { useGetAuditLogs } from '@openapi/logs/logs';
import { useQueryClient } from '@tanstack/react-query';
import UserCommissionGroupAssignments from './components/UserCommissionGroupAssignments';
import {
    getLatestAuditLog,
    getPasswordChangeSource,
    getPasswordChangerLabel,
    PASSWORD_CHANGE_SOURCE,
} from './helpers/passwordAuditLogHelpers';

const USER_TAB = {
    PROFILE: 'profile',
    SECURITY: 'security',
};

function EditUserPage({ newUser }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { venueId, userId } = useParams();
    const { showModal } = useModals();
    const { user: currentUser } = useAuth();
    const { can, CAPABILITIES } = useCapabilities();
    const [roles, setRoles] = useState([]);
    const [generatedPassword, setGeneratedPassword] = useState('');
    const [activeTab, setActiveTab] = useState(USER_TAB.PROFILE);

    const { notifyError, notifySuccess } = useNotify();
    const queryClient = useQueryClient();

    const numericVenueId = useMemo(() => Number(venueId), [venueId]);

    const isExistingUser = !newUser;
    const canCommissionGroupsManage = can(CAPABILITIES.COMMISSION_GROUPS_MANAGE);
    const canUsersManage = can(CAPABILITIES.USERS_MANAGE);

    const defaultRole = 'agent';
    const defaultUserCaps = ROLE_DEFAULTS[defaultRole] || [];
    const defaultUser = {
        name: '',
        email: '',
        role: defaultRole,
        capabilities: [...defaultUserCaps],
        venueId: Number.isFinite(numericVenueId) ? numericVenueId : undefined,
    };

    const { data: userData, isLoading } = useGetUser(Number(userId), Number(venueId), {
        query: {
            enabled: isExistingUser && Boolean(userId) && Boolean(venueId),
            onError: (error) => {
                notifyError(error);
                navigate(`/venues/${venueId}/users`);
            },
        },
    });

    // The acting user can reset password / 2FA for an existing user only when the
    // management hierarchy allows it (mirrors the backend canManageTarget guard).
    const canManagePasswords =
        isExistingUser &&
        canManageTarget(
            currentUser?.capabilities,
            currentUser?.isSuperAdmin,
            userData?.capabilities,
            userData?.isSuperAdmin,
        );

    const editUserMutation = useEditUser();
    const addUserMutation = useAddUser();
    const resetDefaultPasswordMutation = useResetDefaultPassword();
    const forceSetUserPasswordMutation = useForceSetUserPassword();
    const resetTwoFactorMutation = useResetTwoFactor();
    const { data: resetPasswordAuditLogsData, refetch: refetchResetPasswordAuditLogs } =
        useGetAuditLogs(
            {
                limit: 1,
                offset: 0,
                sort: 'created_at',
                sortDir: 'DESC',
                logType: 'reset_password',
                userId: Number(userId),
            },
            {
                query: {
                    enabled:
                        isExistingUser &&
                        canManagePasswords &&
                        can(CAPABILITIES.LOGS_VIEW) &&
                        Number.isFinite(Number(userId)) &&
                        Number.isFinite(Number(venueId)),
                },
            },
        );

    const onEdit = (values) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    const data = {
                        name: values.name,
                        email: values.email,
                    };
                    if (TEMPLATE_ROLES.includes(values.role) || values.role === 'superadmin') {
                        data.role = values.role;
                    }
                    if (values.capabilities) {
                        data.capabilities = values.capabilities;
                    }
                    await editUserMutation.mutateAsync({
                        id: Number(userId),
                        venueId: Number(venueId),
                        data,
                    });
                    await Promise.all([
                        queryClient.invalidateQueries({
                            queryKey: getGetUserQueryKey(Number(userId), Number(venueId)),
                        }),
                        queryClient.invalidateQueries({
                            queryKey: getGetUsersQueryKey(Number(venueId)),
                        }),
                    ]);
                    notifySuccess(t('users.notifications.updated'));
                    navigate(`/venues/${venueId}/users`);
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    const onSubmit = async (values) => {
        if (!newUser) {
            return onEdit(values);
        }
        try {
            const addData = {
                name: values.name,
                email: values.email,
            };
            if (TEMPLATE_ROLES.includes(values.role) || values.role === 'superadmin') {
                addData.role = values.role;
            }
            if (values.capabilities?.length) {
                addData.capabilities = values.capabilities;
            }
            const addedUser = await addUserMutation.mutateAsync({
                id: Number(venueId),
                data: addData,
            });
            if (addedUser.newUser) {
                await resetDefaultPasswordMutation.mutateAsync({
                    data: {
                        email: values.email,
                        venueId: Number(venueId),
                    },
                });
            }
            notifySuccess(t('users.notifications.added'));
            navigate(`/venues/${venueId}/users`);
        } catch (error) {
            notifyError(error);
        }
    };

    const onForceSetRandomPassword = () => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('users.passwordOverride.confirmTitle'),
            content: t('users.passwordOverride.confirmRandom'),
            warningContent: t('users.passwordOverride.warning'),
            confirm: async () => {
                try {
                    const response = await forceSetUserPasswordMutation.mutateAsync({
                        id: Number(userId),
                        venueId: Number(venueId),
                        data: {},
                    });
                    const nextPassword = response.password || '';
                    setGeneratedPassword(nextPassword);
                    await refetchResetPasswordAuditLogs();
                    if (nextPassword) {
                        try {
                            await navigator.clipboard.writeText(nextPassword);
                            notifySuccess(
                                t('users.passwordOverride.notifications.randomSetCopied'),
                            );
                            return;
                        } catch (_copyError) {
                            // no-op; keep showing generated password for manual copy fallback
                        }
                    }
                    notifySuccess(t('users.passwordOverride.notifications.randomSet'));
                } catch (error) {
                    notifyError(error);
                }
            },
        });
    };

    const onResetTwoFactor = () => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('users.resetTwoFactor.title'),
            content: t('users.resetTwoFactor.forceResetDescription'),
            confirm: async () => {
                try {
                    await resetTwoFactorMutation.mutateAsync({
                        data: {
                            userId: Number(userId),
                            venueId: Number(venueId),
                        },
                    });
                    await queryClient.invalidateQueries({
                        queryKey: getGetUserQueryKey(Number(userId), Number(venueId)),
                    });
                    notifySuccess(t('users.notifications.twoFactorReset'));
                } catch (error) {
                    notifyError(error);
                }
            },
        });
    };

    const onCopyGeneratedPassword = async () => {
        try {
            await navigator.clipboard.writeText(generatedPassword);
            notifySuccess(t('users.passwordOverride.notifications.copied'));
        } catch (error) {
            notifyError(error);
        }
    };

    useEffect(() => {
        setGeneratedPassword('');
    }, [userId, venueId]);

    const { lastPasswordChangedAt, lastPasswordChangedBy, lastPasswordChangeSource } =
        useMemo(() => {
            const latestPasswordLog = getLatestAuditLog(resetPasswordAuditLogsData);
            if (!latestPasswordLog) {
                return {
                    lastPasswordChangedAt: null,
                    lastPasswordChangedBy: '',
                    lastPasswordChangeSource: PASSWORD_CHANGE_SOURCE.UNKNOWN,
                };
            }
            return {
                lastPasswordChangedAt: latestPasswordLog.created_at || null,
                lastPasswordChangedBy: getPasswordChangerLabel(latestPasswordLog),
                lastPasswordChangeSource: getPasswordChangeSource(latestPasswordLog),
            };
        }, [resetPasswordAuditLogsData]);

    useEffect(() => {
        if (currentUser?.isSuperAdmin) {
            setRoles(['user', 'superadmin']);
        } else {
            setRoles([]);
        }
    }, [currentUser?.isSuperAdmin]);

    if (isExistingUser && isLoading) {
        return true;
    }

    // Non-superadmins cannot edit superadmin users
    if (isExistingUser && userData?.isSuperAdmin && !currentUser?.isSuperAdmin) {
        return navigate(`/venues/${venueId}/users`);
    }

    // Block new user page if actor has no assignable templates
    if (
        newUser &&
        !currentUser?.isSuperAdmin &&
        getAssignableTemplates(currentUser?.capabilities, false).length === 0
    ) {
        return navigate(`/venues/${venueId}/users`);
    }

    const validateSchema = Yup.object().shape({
        name: Yup.string().required(t('common.required')),
        email: Yup.string().email(t('errors.email')).required(t('common.required')),
        role: Yup.string(),
        venueId: Yup.number().required(t('common.required')),
    });

    const initialValues = isExistingUser
        ? {
              name: userData?.name ?? '',
              email: userData?.email ?? '',
              role: getRoleLabelKey(userData?.capabilities, userData?.isSuperAdmin) || 'agent',
              capabilities: userData?.capabilities ?? [],
              venueId: Number.isFinite(numericVenueId) ? numericVenueId : undefined,
              venues: userData?.venues ?? [],
          }
        : { ...defaultUser };

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={initialValues}
            validationSchema={validateSchema}
            enableReinitialize
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => (
                <Form>
                    <PageWithTitle
                        title={newUser ? t('users.newUserTitle') : `${userData?.name ?? ''}`}
                    >
                        {isExistingUser && (
                            <Grid2 size={{ xs: 12 }}>
                                <Tabs
                                    value={activeTab}
                                    onChange={(_, nextTab) => setActiveTab(nextTab)}
                                    variant="scrollable"
                                    allowScrollButtonsMobile
                                    sx={{
                                        '& .MuiTab-root': { fontWeight: 700 },
                                        '& .MuiTab-root.Mui-selected': { fontWeight: 800 },
                                    }}
                                >
                                    <Tab
                                        value={USER_TAB.PROFILE}
                                        label={t('users.tabs.profileDetails')}
                                    />
                                    <Tab
                                        value={USER_TAB.SECURITY}
                                        label={t('users.tabs.securityAccess')}
                                    />
                                </Tabs>
                            </Grid2>
                        )}
                        <Grid2 size={{ xs: 12, md: 8 }}>
                            {(activeTab === USER_TAB.PROFILE || newUser) && (
                                <EditUserProfileTab
                                    t={t}
                                    values={values}
                                    setFieldValue={setFieldValue}
                                    touched={touched}
                                    errors={errors}
                                    roles={roles}
                                    venueId={venueId}
                                    navigate={navigate}
                                    isSubmitting={isSubmitting}
                                    editUserMutation={editUserMutation}
                                    addUserMutation={addUserMutation}
                                    isSuperAdmin={currentUser?.isSuperAdmin}
                                    currentUserCapabilities={currentUser?.capabilities}
                                    canUsersManage={canUsersManage}
                                    isEditingSelf={
                                        isExistingUser && Number(userId) === currentUser?.id
                                    }
                                />
                            )}
                            {activeTab === USER_TAB.SECURITY && (
                                <EditUserSecurityTab
                                    t={t}
                                    onForceSetRandomPassword={onForceSetRandomPassword}
                                    forceSetUserPasswordMutation={forceSetUserPasswordMutation}
                                    generatedPassword={generatedPassword}
                                    onCopyGeneratedPassword={onCopyGeneratedPassword}
                                    lastPasswordChangedAt={lastPasswordChangedAt}
                                    lastPasswordChangedBy={lastPasswordChangedBy}
                                    lastPasswordChangeSource={lastPasswordChangeSource}
                                    isTwoFactorEnabled={userData?.isTwoFactorEnabled}
                                    onResetTwoFactor={onResetTwoFactor}
                                    isResettingTwoFactor={resetTwoFactorMutation.isPending}
                                    canManagePasswords={canManagePasswords}
                                />
                            )}
                        </Grid2>
                        <Grid2 size={{ xs: 12, md: 4 }}>
                            <BlockWithTitle title={t('venues.title')}>
                                <Grid2 size={{ xs: 12 }} sx={{ textAlign: 'left' }}>
                                    {initialValues?.venues?.length > 0 ? (
                                        initialValues.venues.map((venue) => (
                                            <Chip
                                                key={venue.id}
                                                label={venue.name}
                                                color="primary"
                                                sx={{ mr: 1, mb: 1, maxWidth: '100%' }}
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2">
                                            {t('users.noVenues')}
                                        </Typography>
                                    )}
                                </Grid2>
                            </BlockWithTitle>
                        </Grid2>
                        {activeTab !== USER_TAB.SECURITY &&
                            isExistingUser &&
                            canCommissionGroupsManage && (
                                <Grid2 size={{ xs: 12 }}>
                                    <BlockWithTitle title={t('commissionGroups.assignments')}>
                                        <UserCommissionGroupAssignments
                                            userId={userId}
                                            venueId={venueId}
                                        />
                                    </BlockWithTitle>
                                </Grid2>
                            )}
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default EditUserPage;
